import { useCallback, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Icon } from "../icons";
import type { IconName } from "../icons";
import { Button } from "./Button";
import type { ButtonKind, ButtonSize } from "./Button";
import { Popover } from "./Popover";
import type { PopoverPlacement } from "./Popover";
import { rovingIndex } from "./roving";

export type MenuItem = {
  id: string;
  label: string;
  icon?: IconName | null;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean | null;
  separatorBefore?: boolean;
  run: () => void;
};

export type MenuProps = {
  label: string;
  items: MenuItem[];
  icon?: IconName;
  kind?: ButtonKind;
  size?: ButtonSize;
  placement?: PopoverPlacement;
  className?: string;
  dataFxmap?: string;
  triggerDataFxmap?: string;
  triggerText?: ReactNode;
  pressed?: boolean | null;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function Menu({ label, items, icon = "more", kind = "icon", size = "md", placement = "bottom-end", className, dataFxmap, triggerDataFxmap, triggerText, pressed = null, disabled = false, onOpenChange }: MenuProps) {
  const [open, setOpenState] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>("[role^='menuitem']:not(:disabled)") ?? [])];

    if (buttons.length === 0) return;

    if (event.key === "Tab") {
      close();
      return;
    }

    const index = buttons.findIndex(a => a === document.activeElement);
    const next = rovingIndex(event.key, index, buttons.length, "vertical");

    if (next < 0) return;

    event.preventDefault();
    buttons[next].focus();
  };

  return (
    <span className={["fxmap-popover-host", className ?? ""].filter(Boolean).join(" ")}>
      <Button ref={anchorRef} kind={kind} size={size} icon={icon} label={label} aria-haspopup="menu" aria-expanded={open} pressed={pressed} disabled={disabled} data-fxmap={triggerDataFxmap} onClick={() => setOpen(!open)}>
        {triggerText ?? label}
      </Button>
      <Popover open={open} onClose={close} anchorRef={anchorRef} placement={placement} role="menu" label={label} dataFxmap={dataFxmap}>
        <div ref={listRef} className="fxmap-menu" onKeyDown={onKeyDown}>
          {items.map(item => (
            <button
              key={item.id}
              type="button"
              role={item.checked === null || item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
              aria-checked={item.checked === null || item.checked === undefined ? undefined : item.checked}
              className={["fxmap-menu-item", item.danger ? "fxmap-menu-item-danger" : "", item.separatorBefore ? "fxmap-menu-item-separated" : ""].filter(Boolean).join(" ")}
              disabled={item.disabled}
              data-fxmap-action={item.id}
              onClick={() => {
                close();
                item.run();
              }}
            >
              {item.icon ? <Icon name={item.icon} /> : <span className="fxmap-menu-item-gap" aria-hidden="true" />}
              <span className="fxmap-menu-item-label">{item.label}</span>
              {item.checked ? <Icon name="check" className="fxmap-menu-item-check" /> : null}
            </button>
          ))}
        </div>
      </Popover>
    </span>
  );
}
